import React, { useMemo, useState } from 'react';
import { ArrowUpRight, ChevronDown, Search } from 'lucide-react';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const POSITIONS = ['Attorneys', 'Paralegals', 'Legal assistants', 'Client care & operations'];
const PRACTICES = ['Family Law', 'Criminal Defense', 'Immigration Law'];

export const positionOf = person => /ATTORNEY|PRESIDENT/.test(person.role) ? 'Attorneys' : /PARALEGAL/.test(person.role) ? 'Paralegals'
  : /LEGAL ASSISTANT/.test(person.role) ? 'Legal assistants' : 'Client care & operations';
export const practiceOf = person => /immigration/i.test(`${person.role} ${person.url}`) ? 'Immigration Law'
  : /criminal/i.test(`${person.role} ${person.url}`) ? 'Criminal Defense' : 'Family Law';
const initials = person => person.name.split(' ').filter(part => !/^[A-Z]\.$/.test(part)).map(part => part[0].toUpperCase());

const Select = ({ value, onChange, placeholder, options, testId }) => <label className="team-select">
  <select value={value} onChange={event => onChange(event.target.value)} aria-label={placeholder} data-testid={testId}>
    <option value="">{placeholder}</option>{options.map(option => <option key={option} value={option}>{option}</option>)}
  </select><ChevronDown size={15} aria-hidden="true" />
</label>;

const SearchField = ({ value, onChange, placeholder, testId }) => <label className="team-search">
  <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-label={placeholder} data-testid={testId} /><Search size={17} aria-hidden="true" />
</label>;

export const TeamFilters = ({ filters, setFilters, letters }) => {
  const set = key => value => setFilters(current => ({ ...current, [key]: value }));
  return <div className="team-filters" data-testid="team-filters">
    <div className="team-filter-row">
      <SearchField value={filters.name} onChange={set('name')} placeholder="Search by name" testId="team-search-name" />
      <Select value={filters.position} onChange={set('position')} placeholder="Position" options={POSITIONS} testId="team-filter-position" />
      <Select value={filters.practice} onChange={set('practice')} placeholder="Practice" options={PRACTICES} testId="team-filter-practice" />
    </div>
    <div className="team-filter-row">
      <SearchField value={filters.keyword} onChange={set('keyword')} placeholder="Search by keyword" testId="team-search-keyword" />
    </div>
    <div className="team-letters" role="group" aria-label="Filter by first or last name initial" data-testid="team-letters">
      <button type="button" className={filters.letter ? '' : 'is-active'} onClick={() => set('letter')('')} data-testid="team-letter-all">All</button>
      {LETTERS.map(letter => <button type="button" key={letter} className={filters.letter === letter ? 'is-active' : ''} disabled={!letters.has(letter)} aria-pressed={filters.letter === letter} onClick={() => set('letter')(filters.letter === letter ? '' : letter)} data-testid={`team-letter-${letter}`}>{letter}</button>)}
    </div>
  </div>;
};

const initialFilters = () => {
  const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
  const practice = params.get('practice') || '';
  return { name: '', keyword: '', position: params.get('position') || '', practice: PRACTICES.includes(practice) ? practice : '', letter: '' };
};

export const useTeamFilters = team => {
  const [filters, setFilters] = useState(initialFilters);
  const letters = useMemo(() => new Set(team.flatMap(initials)), [team]);
  const visible = useMemo(() => team.filter(person =>
    (!filters.name || person.name.toLowerCase().includes(filters.name.trim().toLowerCase()))
    && (!filters.keyword || `${person.role} ${person.text}`.toLowerCase().includes(filters.keyword.trim().toLowerCase()))
    && (!filters.position || positionOf(person) === filters.position)
    && (!filters.practice || practiceOf(person) === filters.practice)
    && (!filters.letter || initials(person).includes(filters.letter))), [team, filters]);
  const clear = () => setFilters({ name: '', keyword: '', position: '', practice: '', letter: '' });
  return { filters, setFilters, letters, visible, clear };
};

export const TeamEmpty = ({ clear }) => <p className="team-empty" data-testid="team-empty">No team members match these filters.<button type="button" onClick={clear} data-testid="team-clear-filters">Clear filters<ArrowUpRight size={14} /></button></p>;
